import { useRef, type ReactNode } from 'react';
import styled from 'styled-components';
import { ChatCircleIcon } from '@phosphor-icons/react';
import { CollapsibleSection } from '../../CollapsibleSection';
import { ChatThread, type ChatThreadHandle } from '../../ChatThread';
import { ChatMessage, type ChatMessageAvatar } from '../../ChatMessage';
import { ChatInputBar } from '../../ChatInputBar';
import { ReferenceCards, type ChatSource } from '../ReferenceCards';
import { colors, radii, spacing } from '../../../theme-v2';

const ICON_COLOR = '#34D399';

export interface ChatbotMessage {
  /** Stable identifier — used as the React key. */
  id: string;
  /** Sender role; drives bubble alignment + color. */
  role: 'user' | 'assistant';
  /** Message body (string, JSX, or pre-rendered markdown). */
  body: ReactNode;
  /** Optional pre-formatted timestamp (locale-aware in the caller). */
  timestamp?: string;
  /** Avatar info. Apps typically only pass this for assistant messages. */
  avatar?: ChatMessageAvatar;
  /** When true, renders the typing-indicator placeholder. */
  pending?: boolean;
  /** True while the answer streams; its text is revealed progressively. */
  streaming?: boolean;
  /** Source citations for an assistant answer (rendered as reference cards). */
  sources?: ChatSource[];
  /** True while the answer streams and sources haven't arrived yet. */
  processingSources?: boolean;
}

export interface ChatbotPanelProps {
  /** Section title ("Chatbot"), consumer-translated. */
  title: ReactNode;
  /** Ordered message list — newest at the end. */
  messages: ChatbotMessage[];
  /** Fired when the user submits a non-empty trimmed message. */
  onSubmit: (text: string) => void;
  /** Fired when the input gains focus. */
  onInputFocus?: () => void;
  /**
   * Slot shown inside the thread when `messages` is empty. Typically a
   * `<ChatEmptyState>` with suggested prompts; apps own its props
   * (title, subtitle, prompts).
   */
  emptyState?: ReactNode;
  /** Translated aria-label for the send button (consumer-supplied). */
  sendAriaLabel: string;
  /**
   * Slot shown between the thread and the input — used for the assistant's
   * follow-up suggestions, composed by the app as prompt chips (same
   * `SuggestedPromptChip` as the empty-state prompts). The panel only
   * positions the node; the app owns the chips, mirroring `emptyState`.
   */
  followUps?: ReactNode;
  /** Translated placeholder for the input. */
  placeholder?: string;
  /** While true, input is read-only and submit blocked (streaming response). */
  submitting?: boolean;
  /** Whether the section starts expanded. Defaults to true. */
  defaultOpen?: boolean;
  /** When false, no collapse toggle is offered. `ExperiencePageLayout`
   *  injects this: with no Knowledge Hub beside it, collapsing has nowhere
   *  to go. */
  collapsible?: boolean;
  /** Controlled open state. */
  open?: boolean;
  /** Fired when the user toggles the disclosure. */
  onOpenChange?: (open: boolean) => void;
  /** Fired when a reference card is clicked. */
  onSourceClick?: (source: ChatSource) => void;
  /** Id of the document currently shown — drives the "other document" label. */
  displayedDocumentId?: string;
  /** All documents, to resolve a source's document name. */
  documents?: { id: string; name?: string }[];
  className?: string;
}

/**
 * ChatbotPanel — bottom half of the experience-page sidebar.
 * Composes `CollapsibleSection` ("Chatbot" disclosure) + `ChatThread`
 * (scrollable message list with sticky-bottom behavior) + `ChatInputBar`
 * (auto-grow textarea + send button) + optional `ChatEmptyState` slot.
 *
 * Stateless aside from the imperative scroll handle on the underlying
 * thread (used internally to jump to bottom after submit when the user
 * was already pinned to the latest message — that's handled by
 * `ChatThread`'s sticky behavior).
 *
 * Apps own data: pass `messages` + `onSubmit`. This component owns
 * only the layout, scroll behavior, and the disclosure framing.
 */
export const ChatbotPanel = ({
  title,
  messages,
  onSubmit,
  onInputFocus,
  emptyState,
  followUps,
  sendAriaLabel,
  placeholder,
  submitting,
  defaultOpen = true,
  collapsible = true,
  open,
  onOpenChange,
  onSourceClick,
  displayedDocumentId,
  documents,
  className,
}: ChatbotPanelProps) => {
  const threadRef = useRef<ChatThreadHandle>(null);

  const handleSubmit = (text: string) => {
    onSubmit(text);
    // Force scroll to bottom on user submit so they always see their
    // own message land, even if they were scrolled up reading history.
    threadRef.current?.scrollToBottom('smooth');
  };

  return (
    <CollapsibleSection
      title={title}
      icon={<ChatCircleIcon size={14} weight="fill" color={ICON_COLOR} />}
      defaultOpen={defaultOpen}
      collapsible={collapsible}
      open={open}
      onOpenChange={onOpenChange}
      className={className}
    >
      <StyledBody>
        <StyledThreadWrap>
          <ChatThread ref={threadRef} emptyState={emptyState}>
            {messages.map((m) => (
              <div key={m.id}>
                <ChatMessage
                  role={m.role}
                  avatar={m.avatar}
                  timestamp={m.timestamp}
                  pending={m.pending}
                  streaming={m.streaming}
                  markdown={m.role === 'assistant'}
                >
                  {m.body}
                </ChatMessage>
                {m.role === 'assistant' &&
                (m.sources?.length || m.processingSources) ? (
                  <ReferenceCards
                    sources={m.sources ?? []}
                    processing={m.processingSources}
                    onSourceClick={onSourceClick}
                    displayedDocumentId={displayedDocumentId}
                    documents={documents}
                  />
                ) : null}
              </div>
            ))}
          </ChatThread>
        </StyledThreadWrap>
        {followUps && <StyledFollowUps>{followUps}</StyledFollowUps>}
        <StyledInputWrap>
          <ChatInputBar
            sendAriaLabel={sendAriaLabel}
            placeholder={placeholder}
            onSubmit={handleSubmit}
            onFocus={onInputFocus}
            submitting={submitting}
          />
        </StyledInputWrap>
      </StyledBody>
    </CollapsibleSection>
  );
};

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  /* Grow to fill the CollapsibleSection body (now a flex column).
     Internal scroll lives on ChatThread, so the input bar stays
     pinned at the bottom. */
  flex: 1 1 auto;
  min-height: 0;
  margin: ${spacing.base}px;
  border: 1px solid ${colors.border.subtle};
  border-radius: ${radii.box}px;
  overflow: hidden;
  background: ${colors.surface.base};
`;

const StyledThreadWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const StyledFollowUps = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.sm}px;
  padding: ${spacing.sm}px ${spacing.md}px 0;
  /* SuggestedPromptChip is inline-flex (content-sized) — stretch each to fill
     its grid cell so the chips span the full width, matching the empty state. */
  > button {
    width: 100%;
  }
`;

const StyledInputWrap = styled.div`
  padding: ${spacing.sm}px ${spacing.md}px ${spacing.md}px;
  border-top: 1px solid ${colors.border.subtle};
  background: ${colors.surface.base};
`;
